import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Calendar, Clock, Users, BookOpen, Edit, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentorDashboard() {
  const { mentor, getToken } = useMentorAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getToken();
      const [statsRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/mentor/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/mentor/sessions/upcoming`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setSessions(sessionsRes.data.sessions);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            ¡Bienvenido, {mentor?.name}! 👋
          </h1>
          <p className="text-gray-400">
            Aquí está un resumen de tus sesiones y actividades
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-teal-500/30 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-teal-500/20 rounded-lg">
                <BookOpen className="text-teal-400" size={24} />
              </div>
              <TrendingUp className="text-teal-400" size={20} />
            </div>
            <p className="text-gray-400 text-sm mb-1">Sesiones Totales</p>
            <p className="text-3xl font-bold text-white">{stats?.total_sessions || 0}</p>
          </div>

          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-blue-500/30 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Calendar className="text-blue-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Próximas Sesiones</p>
            <p className="text-3xl font-bold text-white">{stats?.upcoming_sessions || 0}</p>
          </div>

          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-yellow-500/30 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="text-yellow-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Horas Impartidas</p>
            <p className="text-3xl font-bold text-white">{stats?.total_hours || 0}h</p>
          </div>

          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-green-500/30 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <Users className="text-green-400" size={24} />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Estudiantes</p>
            <p className="text-3xl font-bold text-white">{stats?.total_students || 0}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/mentor/profile')}
            className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-xl p-6 text-left hover:border-teal-500/50 transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-teal-500/20 rounded-lg group-hover:bg-teal-500/30 transition-colors">
                <Edit className="text-teal-400" size={24} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">Editar Perfil</h3>
            <p className="text-sm text-gray-400">Actualiza tu información y foto</p>
          </button>

          <button
            onClick={() => navigate('/mentor/sessions')}
            className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-6 text-left hover:border-blue-500/50 transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                <Calendar className="text-blue-400" size={24} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">Ver Sesiones</h3>
            <p className="text-sm text-gray-400">Gestiona tus clases programadas</p>
          </button>

          <button
            onClick={() => navigate('/mentor/stats')}
            className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-6 text-left hover:border-purple-500/50 transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
                <TrendingUp className="text-purple-400" size={24} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">Estadísticas</h3>
            <p className="text-sm text-gray-400">Analiza tu desempeño</p>
          </button>
        </div>

        {/* Próximas Sesiones */}
        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Próximas Sesiones</h2>
            <button
              onClick={() => navigate('/mentor/sessions')}
              className="text-sm text-teal-400 hover:text-teal-300"
            >
              Ver todas →
            </button>
          </div>
          
          {sessions.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 mb-2">No tienes sesiones próximas</p>
              <p className="text-sm text-gray-500">Las sesiones asignadas aparecerán aquí</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.slice(0, 5).map((session) => (
                <div
                  key={session.id}
                  className="bg-black/30 border border-white/10 rounded-lg p-4 hover:border-teal-500/50 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                        {session.title}
                      </h3>
                      <p className="text-sm text-gray-400 mb-3">{session.pilar}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(session.date).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long'
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {session.duration} min
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/mentor/sessions/${session.id}`)}
                      className="flex items-center space-x-2 px-4 py-2 bg-teal-500/10 text-teal-400 rounded-lg hover:bg-teal-500/20 transition-colors"
                    >
                      <Edit size={16} />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
